import { Modal, ModalContent, ModalControls, ModalHeader } from '@upsales/components';
import { asyncModalAdapter } from 'App/helpers/angularPortingHelpers';
import { useTranslation } from 'Components/Helpers/translate';
import React, { useEffect, useMemo, useState } from 'react';
import { ModalProps } from '../Modals/Modals';
import { PrimaryButton, ThirdButton } from '@upsales/components/Buttons';
import openModal from 'App/services/Modal';
import RequestBuilder from 'Resources/RequestBuilder';
import _ from 'lodash';

type Props = ModalProps & {
	account: { name: string; id: number; userRemovable?: boolean };
	accountId: number;
	customerId: number;
	duns: number;
	name?: string;
	dontRedirect?: boolean;
};

export default function ConfirmSoliditetUpdateModal(props: Props) {
	const [buying, setBuying] = useState<boolean>(false);
	const [merging, setMerging] = useState<boolean>(false);
	const [credits, setCredits] = useState<number>(0);
	const [accounts, setAccounts] = useState<any[]>([]);
	const [existingAccounts, setExistingAccounts] = useState<any[]>([]);
	const [currentAccount, setCurrentAccount] = useState<any>(null);
	const [dataLoaded, setDataLoaded] = useState<boolean>(false);

	const { t } = useTranslation();

	const customerId = Tools.AppService.getCustomerId();
	const displayName = props.name ?? props.account.name;

	const properties: any[] = [];

	useEffect(() => {
		const fetchData = async () => {
			try {
				const nameFilter = new RequestBuilder();
				nameFilter.addFilter(
					Tools.SoliditetClient.attr.name,
					nameFilter.comparisonTypes.Search,
					displayName
				);
				nameFilter.limit = 250;
				nameFilter.addSort('_score', false);
				nameFilter.addSort(Tools.SoliditetClient.attr.headquarters, false);

				const accountsRes = await Tools.SoliditetClient.customer(customerId).find(nameFilter.build());
				const accountsData = accountsRes?.data ?? [];

				const dunsFilter = new RequestBuilder();
				const duns = Array.isArray(accountsData)
					? accountsData.map((soliditetClient: any) => soliditetClient.dunsNo)
					: [];
				dunsFilter.addFilter(Tools.Account.attr.dunsNo, nameFilter.comparisonTypes.Equals, duns);
				dunsFilter.addFilter(
					Tools.Account.attr.id,
					nameFilter.comparisonTypes.NotEquals,
					props.accountId
				);
				dunsFilter.limit = 250;
				const existingRes = await Tools.Account.customer(customerId).find(dunsFilter.build());
				const existingAccountsData = existingRes?.data ?? [];

				let currentAccountData = null;
				if (props.duns && props.duns !== 0 && props.duns !== -1) {
					const dunsSearchFilter = new RequestBuilder();
					dunsSearchFilter.addFilter(
						Tools.SoliditetClient.attr.dunsNo,
						dunsSearchFilter.comparisonTypes.Search,
						props.duns
					);
					dunsSearchFilter.limit = 250;
					const currentRes = await Tools.SoliditetClient.customer(customerId).find(
						dunsSearchFilter.build()
					);
					if (currentRes?.data?.length) {
						currentAccountData = currentRes.data[0];
					}
				}

				const metadata = Tools.AppService.getMetadata();
				setCredits(metadata.soliditetCredits);
				setAccounts(accountsData);
				setExistingAccounts(existingAccountsData);
				setCurrentAccount(currentAccountData);
				setDataLoaded(true);
			} catch (error) {
				console.error('Error fetching data:', error);
			}
		};

		fetchData();
	}, []);

	const hasDuns = props.duns && props.duns !== 0 && props.duns !== -1 && currentAccount;

	const hasExisting = useMemo(() => {
		if (!dataLoaded || hasDuns || !props.account.userRemovable) {
			return false;
		}
		let found = false;
		existingAccounts.forEach((existing: any) => {
			// eslint-disable-next-line you-dont-need-lodash-underscore/find
			const match = _.find(accounts, (account: any) => {
				return account.dunsNo === parseInt(existing.dunsNo);
			});
			if (match) {
				found = true;
				match.mergeAble = true;
				match.userEditable = existing.userEditable;
			}
		});
		return found;
	}, [dataLoaded, hasDuns, props.account.userRemovable, existingAccounts, accounts]);

	const stateAccounts = !hasDuns ? accounts : [];

	const options = {
		updateExisting: true,
		skipProjects: true,
		skipAccountManagers: true,
		skipAddresses: false,
		skipCategories: true
	};

	const confirm = () => {
		setBuying(true);
		Tools.SoliditetClient.customer(customerId)
			.refresh(props.accountId, properties, options)
			.then((res: any) => {
				props.close(res.data);
			})
			.catch(() => {
				setBuying(false);
			});
	};

	const handleData = function (account: any) {
		setBuying(true);
		account.$buying = true;

		Tools.Account.customer(customerId)
			.save({ id: props.accountId, dunsNo: Number(account.dunsNo) }, { skipNotification: true })
			.then(() => {
				confirm();
			})
			.catch(function () {
				setBuying(false);
			});
	};

	const onUpdateConfirm = (account: any) => {
		setMerging(true);
		account.$merging = true;

		const mergeAccount = existingAccounts.find((existing: any) => {
			return account.dunsNo === parseInt(existing.dunsNo);
		});

		Tools.Account.customer(customerId)
			.merge(mergeAccount, props.account)
			.then(function () {
				setTimeout(function () {
					if (!props.dontRedirect) {
						Tools.$state.go('account.dashboard', { id: mergeAccount.id });
					} else {
						props.close();
					}
				}, 1500);
			})
			.catch(function () {
				setMerging(false);
			});
	};

	const mergeAccounts = function (account: any) {
		// if multiple show all
		// show modal for confirmation
		let str = t('account.mergeInfo1');
		str += `<b> ${account.name}</b>`;
		str += `<br/><br/>${t('account.mergeInfo2')}`;

		openModal('Alert', {
			title: 'account.mergeClients',
			body: str,
			headerIcon: 'merge',
			confirmButtonText: 'default.merge',
			onClose: confirmed => {
				if (confirmed) {
					onUpdateConfirm(account);
				}
			}
		});
	};

	return (
		<Modal className={props.className}>
			<ModalHeader
				title={t('soliditet.update') + t('soliditet.companyDataFromSoliditet').toLowerCase()}
				onClose={() => props.close()}
			></ModalHeader>
			<ModalContent>
				<form
					autoComplete="off"
					onSubmit={e => {
						e.preventDefault();
						if (hasDuns) {
							confirm();
						}
					}}
				>
					<div className="up-panel">
						<div className="up-panel-content">
							{credits ? (
								<>
									{hasDuns ? (
										<p>
											{t('soliditet.doYouWantToUpdate')} {t('soliditet.companyDataFor').toLowerCase()}{' '}
											<b>{displayName}</b> {t('soliditet.fromSoliditet').toLowerCase()}
											{'?'}
										</p>
									) : (
										<>
											{stateAccounts.length > 0 ? (
												<p>
													<b>{displayName}</b> {t('soliditet.missingDuns')}
												</p>
											) : (
												<p>{t('soliditet.noMatches')}</p>
											)}
											{hasExisting ? (
												<h2
													style={{
														fontSize: '17px',
														marginBottom: '10px',
														marginTop: '10px',
														fontWeight: 'bold'
													}}
												>
													{t('default.existsInUpsales')}
												</h2>
											) : null}
											{stateAccounts.length > 0
												? stateAccounts.map((account: any, index: number) => (
														<div key={index} style={{ marginBottom: '10px' }}>
															{account.mergeAble === true ? (
																<div>
																	{account.userEditable ? (
																		<button
																			type="button"
																			className="pull-right btn up-btn btn-sm btn-bright-blue"
																			disabled={merging}
																			onClick={() => mergeAccounts(account)}
																			style={{ marginTop: '5px' }}
																		>
																			{account.$merging ? (
																				<b
																					className={`fa fa-refresh ${
																						account.$merging ? 'fa-spin' : ''
																					}`}
																				></b>
																			) : null}
																			{t(
																				account.$merging
																					? 'default.merging'
																					: 'default.merge'
																			)}
																		</button>
																	) : null}

																	<span
																		style={{
																			display: 'inline-block',
																			width: '260px',
																			textOverflow: 'ellipsis',
																			whiteSpace: 'nowrap',
																			overflow: 'hidden'
																		}}
																	>
																		{account.name}
																	</span>

																	<br />

																	<span
																		style={{
																			display: 'inline-block',
																			color: '#999'
																		}}
																	>
																		{account.city}
																	</span>

																	{account.isHeadqueraters ? (
																		<b
																			className="fa fa-home"
																			title={t('soliditet.headquarters')}
																		></b>
																	) : null}

																	<div className="clearfix"></div>
																</div>
															) : null}
														</div>
												  ))
												: null}
										</>
									)}

									{stateAccounts.length > 0 &&
									!(hasExisting && stateAccounts.length === 1) ? (
										<h2
											style={{
												fontSize: '17px',
												marginBottom: '10px',
												marginTop: '10px',
												fontWeight: 'bold'
											}}
										>
											{t('default.fromBisnode')}
										</h2>
									) : null}

									{stateAccounts.length > 0
										? stateAccounts.map((account: any, index: number) => (
												<div key={index} className="list-row">
													{!account.mergeAble ? (
														<div>
															<button
																type="button"
																className="pull-right btn up-btn btn-sm btn-bright-blue"
																disabled={buying}
																onClick={() => handleData(account)}
																style={{ marginTop: '5px' }}
															>
																{account.$buying ? (
																	<b
																		className={`fa fa-refresh ${
																			account.$buying ? 'fa-spin' : ''
																		}`}
																	></b>
																) : null}
																{t(account.$buying ? 'default.updating' : 'default.choose')}
															</button>

															<span
																style={{
																	display: 'inline-block',
																	width: '268px',
																	textOverflow: 'ellipsis',
																	whiteSpace: 'nowrap',
																	overflow: 'hidden'
																}}
															>
																{account.name}
															</span>

															<br />

															<span
																style={{
																	display: 'inline-block',
																	color: '#999',
																	textTransform: 'uppercase',
																	fontSize: '12px',
																	letterSpacing: '0.5px'
																}}
															>
																{account.city}
															</span>

															{account.isHeadqueraters ? (
																<b
																	className="fa fa-home text-bright-blue"
																	title={t('soliditet.headquarters')}
																></b>
															) : null}

															<div className="clearfix"></div>
														</div>
													) : null}
												</div>
										  ))
										: null}
								</>
							) : null}

							{hasDuns || (!hasDuns && stateAccounts.length > 0) ? (
								<p style={{ marginTop: '20px' }}>
									{t('soliditet.youHave')} <b className={credits ? 'text-green' : ''}>{credits}</b>{' '}
									{t('soliditet.availableCompanyPurchases').toLowerCase()}
									{'.'}
								</p>
							) : null}
						</div>

						{!credits ? (
							<p style={{ color: 'red' }}>
								{t('soliditet.notEnoughCredits')}
								{'.'}
							</p>
						) : null}
					</div>
				</form>
			</ModalContent>
			<ModalControls>
				<div className="up-modal-controls">
					{hasDuns ? (
						<PrimaryButton
							onClick={() => confirm()}
							className="btn up-btn btn-green"
							disabled={!credits || buying}
						>
							{buying ? <span className={`fa fa-refresh ${buying ? 'fa-spin' : ''}`}></span> : null}
							{t(buying ? 'default.updating' : 'default.update')}
						</PrimaryButton>
					) : null}
					<ThirdButton className="btn up-btn btn-bright-blue btn-link" onClick={() => props.close()}>
						{t('default.abort')}
					</ThirdButton>
				</div>
			</ModalControls>
		</Modal>
	);
}

export const openConfirmSoliditetUpdate = asyncModalAdapter({
	featureFlag: 'OPEN_CONFIRM_SOLIDITET_UPDATE_REACT',
	openModalName: 'ConfirmSoliditetUpdateModal',
	upModalName: 'confirmSoliditetUpdate'
});
